import { FormattedMessage, useIntl } from 'react-intl';

import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import { getPascalCaseMessageByKey } from 'Stores/LanguageStore';
import {
	findAllMeasurementsForFerryTrip,
	getMeasurementLabelFromId,
	getMeasurementValueFromId,
} from 'Util/_HumanWritten/MeasurementUtils';
import { formatWeight, parseWeightUnit } from 'Util/Weight';
import { FormatPhone, upperCaseFirst } from 'Util/StringUtils';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { withLinkedCharacteristics } from 'Modules/Cargo';
import CargoCharacteristicTags from 'Views/Components/_HumanWritten/Tag/CargoCharacteristicTags';
import If from 'Views/Components/If/If';
import { BookingDetailSection, SectionProps } from '../../SharedSections/BookingDetailSection';

export function VehicleInfoSection({ booking, canEdit, onEdit, isInvoiced }: SectionProps) {
	const { driverPhoneEnabled } = appConfigStore;
	const intl = useIntl();
	if (!booking.bookedSummary) {
		console.error('Booked summary missing, this should not happen');
		return <></>;
	}

	const { cargoInfo, driverFirstName, driverLastName, driverPhone, effectiveCharacteristics } = booking.bookedSummary;

	const formatPhone = FormatPhone(driverPhone);
	const addRego = cargoInfo?.cargoIdentification?.toUpperCase() === 'N/A';

	// A characteristic flagged "Link trailer/vehicle" carries between the vehicle and its trailer,
	// so it shows on both sides — merge the trailer's linked characteristics in here (only when a
	// trailer actually exists, so a linked vehicle characteristic doesn't show a phantom tag).
	const hasTrailer = isNotNullOrUndefined(booking.bookedSummary.towOnInfo);
	const vehicleEffectiveCharacteristics = withLinkedCharacteristics(
		effectiveCharacteristics,
		hasTrailer ? booking.bookedSummary.trailerEffectiveCharacteristics : [],
		store.cargoCharacteristicMap,
	);

	let driverInfo = `${upperCaseFirst(driverFirstName)} ${upperCaseFirst(driverLastName)}`;
	if (driverPhoneEnabled && stringNotEmpty(formatPhone)) {
		driverInfo += ` (${formatPhone})`;
	}

	// Show the vehicle / trailer weight only when its type requires an exact
	// weight: master switch on AND (no required characteristics → all bookings,
	// or the type carries a matching characteristic). Exact weight when set,
	// otherwise the selected weight band label.
	const displayUnit = appConfigStore.weightDisplayUnit;

	// Staff+ see the weight whenever the master switch is on (even if the type
	// lacks the required characteristic); customers only see it when the type's
	// characteristics require an exact weight.
	const vehicleCharacteristicIds = store.cargoTypeCharacteristicsMap?.[cargoInfo?.cargoType?.id ?? ''] ?? [];
	// Also show the weight whenever the vehicle is carrying additional weight (a band above the
	// smallest/base weight band — the baseline the breakdown prices from), so it's visible here even
	// when the exact-weight input feature isn't enabled. Weight bands come back sorted ascending.
	const minVehicleWeightValue = findAllMeasurementsForFerryTrip('WEIGHT', 'vehicle')?.[0]?.value ?? 0;
	const vehicleHasAdditionalWeight =
		stringNotEmpty(cargoInfo?.selectedWeightId) &&
		getMeasurementValueFromId(cargoInfo!.selectedWeightId) > minVehicleWeightValue;
	const showVehicleWeight =
		!!cargoInfo &&
		(vehicleHasAdditionalWeight ||
			appConfigStore.cargoTypeRequiresExactWeight(vehicleCharacteristicIds) ||
			(store.isStaff && appConfigStore.vehicleExactWeightInputEnabled));
	let vehicleWeightDisplay = '';
	if (showVehicleWeight && cargoInfo) {
		if (cargoInfo.exactWeight != null && stringNotEmpty(cargoInfo.exactWeightUnit)) {
			vehicleWeightDisplay = formatWeight(
				Number(cargoInfo.exactWeight),
				parseWeightUnit(cargoInfo.exactWeightUnit, displayUnit),
				displayUnit,
			);
		} else {
			// No exact weight entered — show the selected band's weight value converted to the
			// display unit (rather than the band's label).
			vehicleWeightDisplay = formatWeight(
				getMeasurementValueFromId(cargoInfo.selectedWeightId ?? ''),
				'kg',
				displayUnit,
			);
		}
	}

	const vehicleLengthLabel = appConfigStore.vehicleLengthEnabled
		? getMeasurementLabelFromId(cargoInfo?.selectedLengthId ?? '')
		: '';
	// When the vehicle and trailer widths are linked they share one width, so fall back to the
	// trailer's width when the vehicle has none of its own (show it for both).
	const vehicleWidthLabel = getWidthLabelFromId(
		appConfigStore.vehicleAndTrailerWidthLinked
			? (cargoInfo?.selectedWidthId ?? booking.bookedSummary?.towOnInfo?.selectedWidthId)
			: cargoInfo?.selectedWidthId,
	);

	const sectionContent = (
		<>
			<p className="vehicle-rego">
				<If condition={addRego}>
					<span>{getPascalCaseMessageByKey('vehicle-tab__rego__label', 'Rego')} </span>
				</If>
				{cargoInfo?.cargoIdentification?.toUpperCase() || '-'}
				<CargoCharacteristicTags
					effectiveCharacteristics={vehicleEffectiveCharacteristics}
					cargoCharacteristicMap={store.cargoCharacteristicMap}
					respectVisibility
				/>
			</p>
			<p className="vehicle-make-model">{cargoInfo?.cargoType.getFormattedVehicleMakeModel()}</p>
			<DetailLine label="Length" value={vehicleLengthLabel} />
			<DetailLine label="Weight" value={vehicleWeightDisplay} />
			<DetailLine label="Width" value={vehicleWidthLabel} />
			<p className="driver-info">
				<FormattedMessage
					id="vehicle-booking-details__vehicle-contact-details-section__label"
					defaultMessage="Driver"
				/>
				: {driverInfo}
			</p>
		</>
	);

	return (
		<BookingDetailSection
			sectionName={intl.formatMessage({
				id: 'vehicle-booking-details__vehicle-details-section__label',
				defaultMessage: 'Vehicle details',
			})}
			sectionContent={sectionContent}
			sectionClassName="vehicle-details"
			icon={{
				name: 'directions_car',
				useMaterialIcons: true,
			}}
			modificationRules={{
				canEdit: canEdit,
				editType: 'vehicle',
				onEdit: onEdit,
				isInvoiced: isInvoiced,
			}}
		/>
	);
}

/**
 * Returns the width's label for the given id, or an empty string when there is no width.
 */
export function getWidthLabelFromId(widthId?: string): string {
	return store.widths.find(x => x.id === widthId)?.label ?? '';
}

interface DetailLineProps {
	label: string;
	value?: string;
}

/**
 * Renders a "Label: value" line, omitted entirely when the value is empty.
 */
export function DetailLine({ label, value }: DetailLineProps) {
	if (!stringNotEmpty(value)) {
		return null;
	}

	return (
		<p className="booking-detail-line">
			<span className="booking-detail-line__label">{label}:</span> {value}
		</p>
	);
}
